import { useCart } from '@shared/cart-react';
import {
  Button,
  cn,
  Dots,
  Icon,
  Input,
  Modal,
  SmoothMount
} from '@shared/cove-ui';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { createCommaList, roundTo } from '../../../helpers/tools';
import PrettyPrice from '../PrettyPrice';
import {
  type Cart,
  type CouponListVariant,
  filterAvailableCoupons
} from './helpers';

type AvailableCouponsBreakdown = Array<{
  couponCode: string;
  disclaimer?: string;
  totalSavings: number;
  description: string;
}>;

const autoAppliedDiscountIdTitleMap = {
  '141785457': 'First month free',
  '141785456': 'First month free',
  '960179': 'Sale'
};

export const PromoCodeBreakdown = ({
  cart: cartProp,
  autoAppliedDiscountTitle
}: {
  cart?: Cart;
  autoAppliedDiscountTitle?: string;
}) => {
  const cartFromHook = useCart();
  const cart = cartProp ?? cartFromHook;
  const [canRemovePromo, setCanRemovePromo] = useState(true);
  const [showPromoList, setShowPromoList] = useState(false);
  const [promoBusy, setPromoBusy] = useState<string | null>(null);
  const [promoCodesApplied, setPromoCodesApplied] = useState(
    cart.discountBreakdown || []
  );

  const hasPromoCode = promoCodesApplied.length > 0;

  useEffect(() => {
    setPromoCodesApplied(cart?.calculateCouponBreakdown?.() || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.coupons?.length, cart?.orderTotals?.appliedDiscountMap]);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    if (hasPromoCode) {
      timeout = setTimeout(() => {
        setShowPromoList(true);
      }, 200);
    } else {
      if (timeout) {
        clearTimeout(timeout);
      }
      setShowPromoList(false);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [hasPromoCode]);

  const discountPriorityById = useMemo(
    () =>
      new Map(
        (cart.discounts ?? []).map((discount) => [
          discount.id,
          discount.priority ?? 0
        ])
      ),
    [cart.discounts]
  );

  const getAverageDiscountPriority = useCallback(
    (discounts: (typeof promoCodesApplied)[number]['discounts']) => {
      return (
        Object.keys(discounts)
          .map((discountId) => discountPriorityById.get(discountId) ?? 0)
          .reduce((a, b) => a + b, 0) / Object.keys(discounts).length
      );
    },
    [discountPriorityById]
  );

  const sortPromoCodes = useCallback(
    (
      a: (typeof promoCodesApplied)[number],
      b: (typeof promoCodesApplied)[number]
    ) => {
      const discountA = getAverageDiscountPriority(a.discounts);
      const discountB = getAverageDiscountPriority(b.discounts);

      return discountA - discountB;
    },
    [getAverageDiscountPriority]
  );

  return (
    <MotionConfig>
      <AnimatePresence>
        {hasPromoCode && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="xs:mt-cove-15 mt-0 overflow-hidden"
          >
            <div className="p-cove-15 xs:p-cove-25 pt-cove-10 xs:pt-cove-10">
              <h5 className="text-h5">Promo code</h5>
              <div
                className={`mt-cove-10 px-cove-10 overflow-hidden border ${showPromoList ? 'border-cove-dove' : 'border-transparent'} transition-all`}
              >
                <AnimatePresence>
                  {showPromoList &&
                    promoCodesApplied
                      .toSorted(sortPromoCodes)
                      .map(
                        (
                          {
                            couponCodeId,
                            couponCode,
                            couponCodeDisplayText,
                            totalSavings,
                            description
                          },
                          index
                        ) => {
                          const isAutoAppliedEquipment =
                            couponCodeId === 'auto-applied-960179';
                          const isAutoApplied =
                            couponCodeId?.includes('auto-applied');
                          const strippedPromoCodeId = couponCodeId?.replace(
                            'auto-applied-',
                            ''
                          );
                          return (
                            <motion.div
                              key={couponCode}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden"
                            >
                              <div
                                className={cn(
                                  'py-cove-10 bg-white',
                                  index > 0 && 'border-t-cove-dove border-t'
                                )}
                              >
                                <div className="gap-cove-15 md:gap-cove-50 mb-cove-5 flex items-start justify-between text-sm">
                                  <p className="m-0 shrink truncate font-bold">
                                    {isAutoAppliedEquipment &&
                                    autoAppliedDiscountTitle
                                      ? autoAppliedDiscountTitle
                                      : isAutoApplied
                                        ? autoAppliedDiscountIdTitleMap[
                                            strippedPromoCodeId as keyof typeof autoAppliedDiscountIdTitleMap
                                          ] || 'Discount'
                                        : (couponCodeDisplayText || '')
                                              ?.length > 0
                                          ? couponCodeDisplayText
                                          : couponCode}
                                  </p>
                                  {totalSavings > 0 ? (
                                    <p className="text-cove-red m-0 shrink-0 font-normal">
                                      -<PrettyPrice price={totalSavings} />
                                    </p>
                                  ) : null}
                                </div>
                                <div className="gap-cove-50 flex justify-between text-sm">
                                  <p className="m-0">{description}</p>

                                  {!isAutoApplied && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        setCanRemovePromo(false);
                                        setPromoBusy(couponCode);
                                        await cart
                                          .removePromo(couponCode)
                                          .then(() => {
                                            setPromoBusy(null);
                                            setCanRemovePromo(true);
                                          });
                                      }}
                                      disabled={!canRemovePromo}
                                      className="m-0 flex w-max min-w-[55px] flex-shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-inherit underline"
                                    >
                                      {promoBusy === couponCode ? (
                                        <Dots
                                          className="bg-cove-blue h-2 w-2"
                                          gap={3}
                                        />
                                      ) : (
                                        'Remove'
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </motion.div>
                          );
                        }
                      )}
                </AnimatePresence>
              </div>
            </div>
            <span className="bg-cove-dove block h-[1px] w-full" />
          </motion.div>
        )}
      </AnimatePresence>
    </MotionConfig>
  );
};

export const PromoCodeInput = ({
  variant = 'base'
}: {
  variant?: 'base' | 'wide-button';
}) => {
  const [error, setError] = useState<string | null>(null);
  const cart = useCart();
  const form = useForm({
    defaultValues: {
      promo: ''
    }
  });

  const { reset, register, setValue, handleSubmit } = form;

  const onSubmit = async (data: { promo: string }) => {
    await cart
      .applyPromo(data.promo)
      .then((res) => {
        setError(null);
        const defaultHandler = (message: string) => setError(message);

        handleApplyCouponResponse(
          res,
          data.promo.toUpperCase(),
          {
            handler: defaultHandler,
            minimumTotal: (message: string) =>
              toast(message, { duration: 8000 }),
            removedCoupons: (message: string) =>
              toast(message, { duration: 8000 })
          },
          cart
        );

        reset();

        return;
      })
      .catch((err) => console.log({ err }));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="gap-cove-15 flex items-start">
        <Input
          placeholder={
            variant === 'wide-button' ? 'Enter Promo Code' : 'Promo Code'
          }
          {...register('promo', {
            onChange: async (e) => {
              const { value, name } = e.target;
              const normalizedValue = value
                .trim()
                .replace(/[^A-Za-z0-9-]/g, '')
                .toUpperCase();
              setValue(name, normalizedValue);
            }
          })}
          className="rounded-none"
          invalid={!!error}
        />
        <Button
          type="submit"
          variant="outlined"
          disabled={form.formState.isSubmitting}
          loading={form.formState.isSubmitting}
          color={variant === 'wide-button' ? 'navy-blue' : 'blue'}
          className={cn(
            'h-9',
            variant === 'wide-button'
              ? 'w-[160px] border-[1px] font-bold'
              : 'w-[110px] font-medium'
          )}
        >
          Apply
        </Button>
      </div>
      <SmoothMount show={!!error} fade>
        <p className="text-cove-red mb-0 text-sm">{error}</p>
      </SmoothMount>
    </form>
  );
};

type ApplyCouponResponseActions = {
  handler: (message: string) => void;
  exclusive?: (message: string) => void;
  invalid?: (message: string) => void;
  minimumTotal?: (message: string) => void;
  missingProducts?: (message: string) => void;
  noEffect?: (message: string) => void;
  discountsAlreadyApplied?: (message: string) => void;
  alreadyApplied?: (message: string) => void;
  removedCoupons?: (message: string) => void;
  orderTotalTooLow?: (message: string) => void;
  notAvailableInMarket?: (message: string) => void;
};

export const handleApplyCouponResponse = (
  response: Awaited<ReturnType<Cart['applyPromo']>>,
  couponCode: string,
  actions: ApplyCouponResponseActions,
  cart: Cart
) => {
  const {
    isValid,
    isActive,
    allCouponDiscountsAlreadyApplied,
    couponAlreadyApplied,
    missingProducts,
    couponExclusive,
    minimumTotal,
    minimumTotalCoupon,
    hasNoEffect,
    cannotCombineWithAppliedCoupon,
    removedCoupons,
    orderTotalTooLow,
    appliesTo,
    notAvailableInMarket
  } = response;

  const getHandler = (handlerName: keyof ApplyCouponResponseActions) =>
    actions[handlerName] ?? actions.handler;

  if (notAvailableInMarket) {
    const region = cart.country === 'CA' ? 'Canada' : 'the United States';
    getHandler('notAvailableInMarket')(
      `This promo code is not available in ${region}.`
    );
    return;
  }

  if (couponExclusive) {
    getHandler('exclusive')('This coupon cannot be combined with other offers');
    return;
  }

  if (!isActive || !isValid) {
    if (cannotCombineWithAppliedCoupon) {
      getHandler('handler')('Cannot be combined with other coupon codes');
      return;
    }
    getHandler('invalid')(`Please enter a valid promo code`);
    return;
  }

  if (missingProducts.length > 0) {
    const productNames = missingProducts.map((product) => product.name);

    getHandler('missingProducts')(
      `Your cart is not yet eligible for this coupon. Add a ${createCommaList(productNames)} before applying this coupon.`
    );
    return;
  }

  if (orderTotalTooLow && minimumTotal > 0) {
    getHandler('orderTotalTooLow')(
      `This coupon cannot be applied as it requires a minimum order total of $${roundTo(minimumTotal)}.`
    );
    return;
  }

  if (cannotCombineWithAppliedCoupon) {
    getHandler('handler')('Cannot be combined with other coupon codes');
    return;
  }

  if (hasNoEffect && !!minimumTotalCoupon?.minimumTotal) {
    getHandler('noEffect')(
      `"${couponCode.toUpperCase()}" could not be applied because coupon "${minimumTotalCoupon.couponCode}" requires your order total to remain at $${roundTo(minimumTotalCoupon.minimumTotal)} or above. To use "${couponCode.toUpperCase()}", please remove "${minimumTotalCoupon.couponCode}" or add more items to your cart.`
    );
    return;
  }

  if (hasNoEffect) {
    getHandler('handler')(`Please enter a valid promo code`);
    return;
  }

  if (allCouponDiscountsAlreadyApplied) {
    getHandler('discountsAlreadyApplied')(
      'All discounts related to this promo code have already been applied'
    );
    return;
  }

  if (couponAlreadyApplied) {
    getHandler('alreadyApplied')('Promo code already applied');
    return;
  }

  if (
    minimumTotal > 0 &&
    !!minimumTotalCoupon?.minimumTotal &&
    minimumTotalCoupon.minimumTotal >= (cart.totals?.totals?.orderTotal || 0) &&
    !appliesTo?.includes('subscription')
  ) {
    getHandler('minimumTotal')(
      `The coupon has been applied, but ${minimumTotalCoupon.couponCode !== couponCode ? `coupon "${minimumTotalCoupon.couponCode}"` : 'it'} requires a minimum order total of $${minimumTotalCoupon.minimumTotal}. Your order total has been updated.${removedCoupons.length > 0 ? ` ${createCommaList(removedCoupons)} ${removedCoupons.length === 1 ? 'has' : 'have'} been removed because ${removedCoupons.length === 1 ? 'it' : 'they'} had a lower priority or ${removedCoupons.length === 1 ? 'was' : 'were'} not removing anything from the total price.` : ''}`
    );
  }

  cart.calculateCouponBreakdown();

  return;
};

export const AvailablePromotions = ({
  variant
}: {
  variant?: CouponListVariant;
}) => {
  const [expanded, setExpanded] = useState(false);
  const cart = useCart();

  const availableCoupons = filterAvailableCoupons(
    cart.availableCoupons,
    variant
  );

  if (!availableCoupons?.length) return null;

  return (
    <div className="mt-cove-10 sm:mt-cove-15 flex w-full flex-col">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="text-cove-blue flex items-center bg-transparent text-sm font-medium"
      >
        See Available Promotions
        <Icon
          className={cn(
            'transition-all duration-500 ease-in-out',
            expanded && 'rotate-180'
          )}
          icon="Caret"
        />
      </button>
      <SmoothMount
        transition={{
          type: 'spring',
          bounce: 0,
          duration: Math.max(0.3, availableCoupons?.length * 0.15)
        }}
        show={expanded}
      >
        {availableCoupons?.map((coupon) => {
          return (
            <div
              key={coupon.couponCode}
              className="gap-cove-15 pt-cove-5 flex flex-col"
            >
              <Promotion coupon={coupon} />
            </div>
          );
        })}
      </SmoothMount>
    </div>
  );
};

const Promotion = ({
  coupon
}: {
  coupon: AvailableCouponsBreakdown[number];
}) => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [loading, setLoading] = useState(false);

  const cart = useCart();
  const couponApplied = cart.coupons.some(
    (cpn) => cpn.couponCode === coupon.couponCode.toUpperCase()
  );

  const handleTogglePromo = () => {
    setLoading(true);
    if (couponApplied) {
      cart.removePromo(coupon.couponCode).then(() => setLoading(false));
    } else {
      cart
        .applyPromo(coupon.couponCode)
        .then((res) => {
          const handler = (message: string) => toast(message);

          handleApplyCouponResponse(
            res,
            coupon.couponCode.toUpperCase(),
            {
              handler
            },
            cart
          );
          return;
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div className="p-cove-15 border-cove-navy-blue/25 gap-cove-5 flex w-full flex-col border">
      <div className="flex w-full items-center justify-between">
        <div className="gap-cove-5 flex items-center justify-start">
          <p className="text-cove-navy-blue text-sm font-semibold">
            {coupon.couponCode}
          </p>
          {coupon.disclaimer && (
            <Modal open={showDisclaimer} onOpenChange={setShowDisclaimer}>
              <Modal.Trigger>
                <Button
                  variant="unstyled"
                  className="mb-cove-2"
                  onClick={() => {
                    setShowDisclaimer(true);
                  }}
                >
                  <Icon
                    icon="Info"
                    width={12}
                    height={12}
                    className="text-black/20"
                  />
                  <p className="sr-only">{coupon.couponCode} disclaimer</p>
                </Button>
              </Modal.Trigger>
              <Modal.Content className="w-[95%] rounded sm:w-auto">
                <div className="p-cove-25 flex items-center justify-center rounded">
                  {coupon.disclaimer}
                </div>
              </Modal.Content>
            </Modal>
          )}
        </div>
        <p
          className={cn(
            'text-sm font-semibold',
            coupon.totalSavings > 0 ? 'text-cove-red' : 'text-cove-navy-blue'
          )}
        >
          {coupon.totalSavings > 0 ? <>-${coupon.totalSavings}</> : 'FREE'}
        </p>
      </div>
      <div className="gap-cove-10 flex w-full items-center justify-between">
        <p className="text-cove-navy-blue text-[12px] font-normal">
          {coupon.description}
        </p>
        <Button
          variant="outlined"
          className={cn(
            'text-cove-navy-blue border-cove-navy-blue py-cove-2 gap-cove-5 max-w-cove-100 flex h-6 items-center justify-center rounded-full border bg-[#F9FDFD] px-2 text-[12px] font-semibold',
            couponApplied && 'bg-cove-navy-blue text-white'
          )}
          onClick={handleTogglePromo}
          disabled={loading}
        >
          {loading ? (
            <Dots
              className={cn('w-cove-5 h-cove-5', couponApplied && 'bg-white')}
            />
          ) : (
            <>
              <Icon
                icon="Check"
                className={cn(
                  'text-cove-navy-blue hidden max-w-3',
                  couponApplied && 'flex'
                )}
              />
              {couponApplied ? 'Redeemed' : 'Redeem'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
